import React from "react";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * One KPI tile: soft icon chip, title, large value, subtext.
 *
 * THEME
 * -----
 * Surfaces, text and borders are semantic tokens (`bg-card`, `text-card-foreground`,
 * `border-border`, `text-muted-foreground`), so both themes come from the palette the
 * rest of admin already uses rather than a second set of colours defined here. The
 * accent is the one deliberate exception: a chip needs a hue, and each is given as a
 * light/dark PAIR (`bg-blue-50 dark:bg-blue-500/10`) — a single light-only value would
 * be a white square on the dark page.
 *
 * `value === null` renders "—". That is load-bearing: a KPI whose source failed must
 * not show CHF 0, which is a real and very different statement.
 */

const ACCENTS = {
  blue: "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400",
  indigo: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400",
  amber: "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400",
  emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400",
  sky: "bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400",
  rose: "bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400",
  violet: "bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400",
  teal: "bg-teal-50 text-teal-600 dark:bg-teal-500/10 dark:text-teal-400",
};

export default function OverviewKpiCard({
  title,
  value,
  subtext,
  icon,
  accent = "blue",
  loading = false,
  error = "",
  compact = false,
  onRetry,
  footer,
  testId,
}) {
  const chip = ACCENTS[accent] || ACCENTS.blue;

  return (
    <div
      data-testid={testId}
      aria-busy={loading || undefined}
      className="rounded-xl border border-border bg-card p-4 shadow-sm transition-colors hover:border-border/80"
    >
      <div className="flex items-start gap-2.5">
        {icon ? (
          <span
            aria-hidden
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${chip}`}
          >
            {icon}
          </span>
        ) : null}
        <span className="mt-1 text-[11px] font-medium leading-tight text-muted-foreground">
          {title}
        </span>
      </div>

      <div className="mt-3">
        {loading ? (
          <Skeleton className={compact ? "h-6 w-24" : "h-8 w-32"} />
        ) : (
          <div
            className={`font-semibold tracking-tight text-card-foreground ${
              compact ? "text-[20px]" : "text-[26px]"
            }`}
          >
            {value === null || value === undefined ? "—" : value}
          </div>
        )}
      </div>

      {error ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {/* Text, not colour alone: a red tint is invisible to some readers and
              indistinguishable from an accent to others. */}
          <span className="text-[11px] text-destructive">Nicht verfügbar</span>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            >
              Erneut laden
            </button>
          ) : null}
        </div>
      ) : subtext ? (
        <div className="mt-2 text-[11px] leading-snug text-muted-foreground">{subtext}</div>
      ) : null}

      {/* Slot for a card-specific action — the Barkautionen card uses it to keep the
          existing details modal reachable. Hidden while loading or failed, where
          there is nothing to drill into. */}
      {footer && !loading && !error ? <div className="mt-2">{footer}</div> : null}
    </div>
  );
}
